/* eslint-disable max-len */
import {
	AnonymousStudentProfileSynced,
	AnonymousStudentProfileUpdated,
	StudentRepositoryStateType
} from '@studyportals/anonymous-student-interfaces';
import { DataLayerPageViewDispatched, IOrganisation, IStudy } from '@studyportals/datalayer';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { SessionDestroyedEvent, WishlistSyncedEvent } from '@studyportals/student-interfaces';
import { FavouriteAdded, FavouriteRemoved } from '@studyportals/wishlist-service-core';
import CIOTracker from './CIOTracker';
import { StudentField } from '@studyportals/studentdomain';
import { EligibilityUpdateEvent } from '@studyportals/eligibility-model';

export default class CIOSubscriber
	implements
		ISubscriber<
			| AnonymousStudentProfileSynced
			| AnonymousStudentProfileUpdated
			| SessionDestroyedEvent
			| DataLayerPageViewDispatched
			| FavouriteAdded
			| FavouriteRemoved
			| WishlistSyncedEvent
		>
{
	private eventAggregationService = window.EventAggregationService;

	constructor(private tracker: CIOTracker) {
		this.setupSubscriptions();
	}

	private setupSubscriptions(): void {
		this.eventAggregationService.subscribeTo(AnonymousStudentProfileSynced.EventType, this, false);
		this.eventAggregationService.subscribeTo(AnonymousStudentProfileUpdated.EventType, this, false);
		this.eventAggregationService.subscribeTo(SessionDestroyedEvent.EventType, this, false);
		this.eventAggregationService.subscribeTo(DataLayerPageViewDispatched.EventType, this, true);
		this.eventAggregationService.subscribeTo(FavouriteAdded.EventType, this, true);
		this.eventAggregationService.subscribeTo(FavouriteRemoved.EventType, this, false);
		this.eventAggregationService.subscribeTo(WishlistSyncedEvent.EventType, this, true);
		this.eventAggregationService.subscribeTo(EligibilityUpdateEvent.EventType, this, true);

		void this.handleInitialPageLoad();
	}

	async notify(
		event:
			| AnonymousStudentProfileSynced
			| AnonymousStudentProfileUpdated
			| SessionDestroyedEvent
			| DataLayerPageViewDispatched
			| FavouriteAdded
			| FavouriteRemoved
			| WishlistSyncedEvent
			| EligibilityUpdateEvent
	): Promise<void> {
		if (event.eventType === AnonymousStudentProfileSynced.EventType) {
			await this.handleAnonymousStudentProfileSyncedEvent(event as AnonymousStudentProfileSynced);
		}

		if (
			event.eventType === AnonymousStudentProfileUpdated.EventType &&
			(event as AnonymousStudentProfileUpdated).isLocal
		) {
			this.handleAnonymousStudentProfileUpdatedEvent(event as AnonymousStudentProfileUpdated);
		}

		if (event.eventType === SessionDestroyedEvent.EventType) {
			this.handleSessionDestroyedEvent();
		}

		if (event.eventType === DataLayerPageViewDispatched.EventType) {
			await this.handleDataLayerPageViewDispatched(event as DataLayerPageViewDispatched);
		}

		if (event.eventType === FavouriteAdded.EventType && (event as FavouriteAdded).isLocal) {
			this.handleFavouriteAddedEvent(event as FavouriteAdded);
		}

		if (event.eventType === FavouriteRemoved.EventType && (event as FavouriteRemoved).isLocal) {
			this.handleFavouriteRemovedEvent(event as FavouriteRemoved);
		}

		if (event.eventType === WishlistSyncedEvent.EventType) {
			await this.handleWishlistSyncedEvent();
		}

		if (event.eventType === EligibilityUpdateEvent.EventType) {
			this.handleEligibilityUpdateEvent(event as EligibilityUpdateEvent);
		}
	}

	private async handleInitialPageLoad(): Promise<void> {
		await this.tracker.handleUserIdentification();
	}

	private async handleAnonymousStudentProfileSyncedEvent(event: AnonymousStudentProfileSynced): Promise<void> {
		if (event.state === StudentRepositoryStateType.ONLINE) {
			await this.tracker.handleUserIdentification(true);
			await this.tracker.syncInitialInterests();
		}
	}

	private async handleWishlistSyncedEvent(): Promise<void> {
		await this.tracker.syncFavourites();
	}

	private handleAnonymousStudentProfileUpdatedEvent(event: AnonymousStudentProfileUpdated): void {
		if (event.changes[StudentField.INTERESTS_DISCIPLINES] || event.changes[StudentField.INTERESTS_COUNTRIES]) {
			this.tracker.trackUpdatedInterests(event.changes);
		}
	}

	public handleEligibilityUpdateEvent(event: EligibilityUpdateEvent): void {
		if (!event.updatedRequirements) {
			return;
		}

		this.tracker.trackScholarshipEligibilityEvent(event.updatedRequirements);
	}

	private handleSessionDestroyedEvent(): void {
		this.tracker.resetCIOIdentity();
	}

	private async handleDataLayerPageViewDispatched(event: DataLayerPageViewDispatched): Promise<void> {
		if (event.study) {
			this.tracker.trackStudyPageEvents(event.study as IStudy);
		}

		if (event.organisation) {
			this.tracker.trackOrganisationPageEvents(event.organisation as IOrganisation);
		}
	}

	private handleFavouriteAddedEvent(event: FavouriteAdded): void {
		this.tracker.trackFavouriteAdded(event);
	}

	private handleFavouriteRemovedEvent(event: FavouriteRemoved): void {
		this.tracker.trackFavouriteRemoved(event);
	}
}
